import {observer} from "mobx-react";
import _ from "lodash";
import React from "react";
import {callWithCatch, matchFormat, pp} from "../utils";
import {
    notification,
    Select,
    Space,
    Button
} from "antd";
import FakeTable from "./FakeTable";
import NiceModal from "@ebay/nice-modal-react";
import {NiceModalForm} from "./Modals";
import {Switch2} from "./Switcher";
import ScoreBtn from "@/components/ScoreBtn.js";
import Tourney from "@/apps/tourney/Tourney.js";
import {observable} from "mobx";

let MPlayerSelector = observer(({m, ti, i, letter}) => {
    let onChange = id => {
        m.player_ids[ti][i] = id;
    }

    // Get all selected player IDs from all positions to exclude them from options
    let allSelectedIds = [
        ...m.player_ids[0], // Team 1 selected players
        ...m.player_ids[1]  // Team 2 selected players
    ].filter(id => id !== null && id !== undefined);

    return <Space>
        {letter}
        <PlayerSelector t={m.teams[ti]} value={m.player_ids[ti][i]} onChange={onChange} excludeIds={allSelectedIds} currentId={m.player_ids[ti][i]} />
    </Space>
});

let PlayerSelector = observer(({t, value, onChange, excludeIds = [], currentId, gender, size}) => {
    let players = t.players;

    // Filter by gender if specified
    if (gender) {
        players = players.filter(pl => pl.gender === gender);
    }

    // Filter out already selected players, but keep the current selection available
    let availablePlayers = players.filter(pl =>
        !excludeIds.includes(pl.id) || pl.id === currentId
    );

    let pl = value && _.find(players, { id: value });

    return <Select
        onChange={onChange}
        value={pl?.id}
        style={{width: "200px"}}
        size={size}
        allowClear
        options={availablePlayers.map(pl => ({ label: pl.name, value: pl.id }))}
    />;
});

let whoWonGame = (g, scoreMax=11, by=2) => {
    let t1p = g[0];
    let t2p = g[1];

    if (typeof(t1p) != 'number' || typeof(t2p) != 'number')
        return null;
    else if (t1p === t2p)
        return null;
    else if (t1p < scoreMax && t2p < scoreMax)
        return null;
    else if ((t1p > scoreMax || t2p > scoreMax) && Math.abs(t1p - t2p) !== by)
        return null;
    else if (t1p > t2p)
        return 0;
    else
        return 1;
}

function isTie(games) {
    let tally = _.countBy(games, g => whoWonGame(g));

    return tally[0] === tally[1];
}

let GameScoreBtn = observer(({games, gi, ti, by=2, max=11, disabled: parentDisabled}) => {
    let mainGames = games.slice(0, 2);
    let tiebreakerGame = gi === 2;

    let disabled = parentDisabled || (tiebreakerGame && (_.some(mainGames, g => g[0] === null) || !isTie(mainGames)));

    let g = games[gi];
    let score = g[ti];
    let oti = ti === 0 ? 1 : 0;
    let oscore = g[oti];

    let setScore = (t, n) => {
        g[ti] = n;

        if (_.isNumber(n)) {
            if (!_.isNumber(oscore))
                g[oti] = Math.max(max, n+by);
        }
        else {
            g[oti] = null;
        }

        if (!tiebreakerGame) {
            games[2][0] = games[2][1] = null;
        }
    }

    let type;
    let winner = whoWonGame(g, max, by);
    if (winner === ti)
        type = "score-btn-win";
    if (winner === oti)
        type = "score-btn-lose";

    return <ScoreBtn admin={true} disabled={disabled} key={g.id} score={score} onChange={setScore} max={12} type={type}/>
});

let SingleGameScoreBtn = observer(({g, ti, by=2, max=11, disabled: parentDisabled}) => {
    let score = g[ti];
    let oti = ti === 0 ? 1 : 0;
    let oscore = g[oti];

    let setScore = (t, n) => {
        g[ti] = n;

        if (_.isNumber(n)) {
            if (!_.isNumber(oscore))
                g[oti] = Math.max(max, n+by);
        }
        else {
            g[oti] = null;
        }
    }

    let type;
    let winner = whoWonGame(g, max, by);
    if (winner === ti)
        type = "score-btn-win";
    if (winner === oti)
        type = "score-btn-lose";

    return <ScoreBtn admin={true} key={g.id} score={score} onChange={setScore} max={max+1} type={type}/>
});

let PmatchStatusBtn = observer(({pmatch, teamIndex}) => {
    let pmatchStatus = pmatch?.status || 'scheduled';
    let teamPrefix = teamIndex === 0 ? 't1' : 't2';

    let value = '';
    if (pmatchStatus === `${teamPrefix}_forfeit`) value = 'forfeit';
    if (pmatchStatus === `${teamPrefix}_withdraw`) value = 'withdraw';

    return <Select
        size="small"
        value={value}
        style={{width: "100%"}}
        allowClear
        onChange={(value) => {
            pmatch.status = value ? `${teamPrefix}_${value}` : 'scheduled';
        }}
        options={[
            {value: 'forfeit', label: 'Forfeit'},
            {value: 'withdraw', label: 'Withdraw'}
        ]}
    />
});

let SubModal = NiceModal.create(observer(({player, allPlayers, subs, matchId, onUpdate}) => {
    let sub = subs.find(s => s.player_id === player.id);
    let [selectedPlayerId, setSelectedPlayerId] = React.useState(sub?.sub_player_id);

    let onSave = async () => {
        if (!selectedPlayerId) return;

        // Update local subs array
        let existingSub = subs.find(s => s.player_id === player.id);
        if (existingSub) {
            existingSub.sub_player_id = selectedPlayerId;
        } else {
            subs.push({player_id: player.id, sub_player_id: selectedPlayerId});
        }

        // Save to server
        await callWithCatch({
            url: "/tmatches/" + matchId,
            method: "put",
            params: {
                subs: subs
            },
            onSuccess: data => {
                notification.success({ message: `Substitution saved!` });
                NiceModal.remove(SubModal);
                if (onUpdate) onUpdate(data.reload_info);

                // Close and reopen match editor with fresh data
                NiceModal.remove(MatchEditor);
                NiceModal.show(MatchEditor, {m: data.match, onUpdate});
            },
        });
    };

    return <NiceModalForm
        onSave={onSave}
        title={`sub for ${player.name}`}
        width={400}
        okText="Save"
    >
        <PlayerSelector
            t={{players: allPlayers}}
            value={selectedPlayerId}
            onChange={setSelectedPlayerId}
            gender={player.gender}
        />
    </NiceModalForm>;
}));

let PlayerWithSub = observer(({player, team, allPlayers, subs, matchId, onUpdate}) => {
    // Check if this player IS a substitute (is_sub flag from backend)
    // OR if this player has a sub (for original players not yet replaced in UI)
    let isSub = player.is_sub;
    let sub = subs.find(s => s.player_id === player.id);

    let removeSub = async () => {
        // If this player is a substitute, remove by original_player_id
        // Otherwise, remove by player.id
        let index = isSub
            ? subs.findIndex(s => s.player_id === player.original_player_id)
            : subs.findIndex(s => s.player_id === player.id);

        if (index !== -1) {
            subs.splice(index, 1);
        }

        // Save to server
        await callWithCatch({
            url: "/tmatches/" + matchId,
            method: "put",
            params: {
                subs: subs
            },
            onSuccess: data => {
                notification.success({ message: `Substitution removed!` });
                if (onUpdate) onUpdate(data.reload_info);

                // Close and reopen match editor with fresh data
                NiceModal.remove(MatchEditor);
                NiceModal.show(MatchEditor, {m: data.match, onUpdate});
            },
        });
    };

    let openSubModal = () => {
        // Pass the original player ID if this is a substitute
        let originalPlayer = isSub ? {id: player.original_player_id, name: player.name.replace('*', ''), gender: player.gender} : player;
        NiceModal.show(SubModal, {player: originalPlayer, allPlayers, subs, matchId, onUpdate});
    };

    return <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <span>{player.name}</span>
        {(isSub || sub) ? (
            <a
                onClick={removeSub}
                className="text-xs text-red-500"
                style={{cursor: 'pointer'}}
            >
                remove sub
            </a>
        ) : (
            Tourney.hopa && <a
                onClick={openSubModal}
                className="text-xs text-blue-500"
                style={{cursor: 'pointer'}}
            >
                sub player
            </a>
        )}
    </div>;
});

let MatchEditor = NiceModal.create(observer(({ m, onUpdate }) => {
    pp(m)
    if (m.kind === "mw_mx4")
        return <MwMx4MatchEditor m={m} onUpdate={onUpdate} />;
    else if (m.kind === "xpmatches")
        return <XpmatchesMatchEditor m={m} onUpdate={onUpdate} />;
    else if (Tourney.joola)
        return <JoolaMatchEditor m={m} onUpdate={onUpdate} />;
    else
        return <SingleGameMatchEditor m={m} onUpdate={onUpdate} />;
}));

let SingleGameMatchEditor = observer(({ m, onUpdate }) => {
    let onSave = async () => {
        let pmatchStatuses = m.pmatches.map(pm => pm?.status || 'scheduled');

        await callWithCatch({
            url: "/tmatches/" + m.id,
            method: "put",
            params: {
                scores: m.scores,
                player_ids: m.player_ids,
                pmatch_statuses: pmatchStatuses
            },
            onSuccess: data => {
                onUpdate(data.reload_info);
                notification.success({ message: `Match Saved!` });

                NiceModal.remove(MatchEditor);
            },
        })
    }

    let g = m.scores[0];

    let max = m.format === "to_15" ? 15 : 11;

    return <NiceModalForm onSave={onSave}
                          title={m.name + " (#" + m.mid + ")"}
                          top
                          width={400}
                          okText="Save"
                          vertical
                          initialValues={{}}>
        <FakeTable small>
            <tr>
                <td style={{width: "100%"}}><TeamName t={m.teams[0]} /></td>
                <td><SingleGameScoreBtn g={g} ti={0} max={max}/></td>
            </tr>
            <tr>
                <td style={{width: "100%"}}><TeamName t={m.teams[1]} /></td>
                <td><SingleGameScoreBtn g={g} ti={1}  max={max}/></td>
            </tr>
        </FakeTable>
    </NiceModalForm>
});

let JoolaMatchEditor = observer(({ m, onUpdate }) => {
    let teams = m.teams;
    let t1 = teams[0];
    let t2 = teams[1];

    let allPlayers = _.keyBy(t1.players.concat(t2.players), 'id');

    let onSave = async () => {
        // Prepare pmatch statuses
        let pmatchStatuses = m.pmatches.map(pm => pm?.status || 'scheduled');
        
        await callWithCatch({
            url: "/tmatches/" + m.id,
            method: "put",
            params: { 
                scores: m.scores, 
                player_ids: m.player_ids,
                pmatch_statuses: pmatchStatuses
            },
            onSuccess: data => {
                onUpdate(data.reload_info);
                notification.success({ message: `Match Saved!` });

                NiceModal.remove(MatchEditor);
            },
        })
    }

    return <NiceModalForm onSave={onSave}
                          title={m.name + " (#" + m.mid + ")"}
                          top
                          width={650}
                          okText="Save"
                          vertical
                          initialValues={{t1_id: teams[0].id, t2_id: teams[1].id}}>
        <FakeTable small>
            <tr className="bold">
                <td>{t1.name}</td>
                <td>{t2.name}</td>
            </tr>
            <tr>
                <td className="space-y-2">
                    <MPlayerSelector m={m} ti={0} i={0} letter="A"/>
                    <MPlayerSelector m={m} ti={0} i={1} letter="B"/>
                    <MPlayerSelector m={m} ti={0} i={2} letter="C"/>
                </td>
                <td className="space-y-2">
                    <MPlayerSelector m={m} ti={1} i={0} letter="X"/>
                    <MPlayerSelector m={m} ti={1} i={1} letter="Y"/>
                    <MPlayerSelector m={m} ti={1} i={2} letter="Z"/>
                </td>
            </tr>
        </FakeTable>
        <br/>
        <div className="space-y-4">
            {[1,2,3].map(mn => {
                let hash = [[0, 1], [1, 2], [2, 0]];
                let pm = m.scores[mn-1];
                
                let pmatch = m.pmatches[mn-1];

                return <FakeTable key={mn} small headers={["Match " +mn, "Game 1", "Game 2", "Game 3", "Status"]}>
                    <tr>
                        <td style={{width: "200px"}}>
                            {hash[mn-1].map(j => <div key={j}><span className="text-slate-400 pr-1">{"ABC"[j]}</span> {allPlayers[m.player_ids[0][j]]?.name}</div>)}
                        </td>
                        <td><GameScoreBtn games={pm} gi={0} ti={0} /></td>
                        <td><GameScoreBtn games={pm} gi={1} ti={0} /></td>
                        <td><GameScoreBtn games={pm} gi={2} ti={0} /></td>
                        <td style={{width: "120px"}}>
                            <PmatchStatusBtn pmatch={pmatch} teamIndex={0} />
                        </td>
                    </tr>
                    <tr>
                        <td style={{width: "200px"}}>
                            {hash[mn-1].map(j => <div key={j}><span className="text-slate-400 pr-1">{"XYZ"[j]}</span> {allPlayers[m.player_ids[1][j]]?.name}</div>)}
                        </td>
                        <td><GameScoreBtn games={pm} gi={0} ti={1} /></td>
                        <td><GameScoreBtn games={pm} gi={1} ti={1} /></td>
                        <td><GameScoreBtn games={pm} gi={2} ti={1} /></td>
                        <td style={{width: "120px"}}>
                            <PmatchStatusBtn pmatch={pmatch} teamIndex={1} />
                        </td>
                    </tr>
                </FakeTable>
            })}
        </div>
    </NiceModalForm>
});

let XpmatchesMatchEditor = observer(({ m, onUpdate }) => {
    // Initialize subs state - make it observable
    if (!m.subs) {
        m.subs = observable([]);
    }

    let onSave = async () => {
        let pmatchStatuses = m.pmatches.map(pm => pm?.status || 'scheduled');

        await callWithCatch({
            url: "/tmatches/" + m.id,
            method: "put",
            params: {
                scores: m.scores,
                player_ids: m.player_ids,
                pmatch_statuses: pmatchStatuses,
                pmatches: m.pmatches.map(pm => ({
                    id: pm.id,
                    scores: pm.scores,
                    player_ids: (pm.player_ids || []).map(id => id || 0),
                    status: pm.status
                }))
            },
            onSuccess: data => {
                onUpdate(data.reload_info);
                notification.success({ message: `Match Saved!` });

                NiceModal.remove(MatchEditor);
            },
        })
    }

    let max = m.format === "to_15" ? 15 : 11;

    return <NiceModalForm onSave={onSave}
                          title={m.name + " (#" + m.mid + ")"}
                          top
                          width={800}
                          okText="Save"
                          vertical
                          initialValues={{}}>
        <FakeTable small headers={["Game", m.teams[0].name, "Score", "Score", m.teams[1].name]}>
            {m.pmatches.map((pmatch, gameIndex) => (
                <tr key={gameIndex}>
                    <td style={{width: "100px"}}>Game #{gameIndex + 1}</td>
                    <td style={{width: "300px"}}>
                        <div className="space-y-2">
                            <PlayerSelector t={m.teams[0]} value={pmatch.player_ids[0]}
                                          onChange={id => pmatch.player_ids[0] = id}
                                           />
                            <PlayerSelector t={m.teams[0]} value={pmatch.player_ids[1]}
                                          onChange={id => pmatch.player_ids[1] = id}
                                           />
                        </div>
                    </td>
                    <td><SingleGameScoreBtn g={pmatch.scores[0]} ti={0} max={max} by={1}/></td>
                    <td><SingleGameScoreBtn g={pmatch.scores[0]} ti={1} max={max} by={1}/></td>
                    <td style={{width: "300px"}}>
                        <div className="space-y-2">
                            <PlayerSelector t={m.teams[1]} value={pmatch.player_ids[2]}
                                          onChange={id => pmatch.player_ids[2] = id}
                                           />
                            <PlayerSelector t={m.teams[1]} value={pmatch.player_ids[3]}
                                          onChange={id => pmatch.player_ids[3] = id}
                                           />
                        </div>
                    </td>
                </tr>
            ))}
        </FakeTable>
    </NiceModalForm>
});

let MwMx4MatchEditor = observer(({ m, onUpdate }) => {
    // Initialize subs state - make it observable
    if (!m.subs) {
        m.subs = observable([]);
    }

    // Get substitution list from tournament sublist or fall back to team players
    let allPlayers = Tourney.sublist && Tourney.sublist.length > 0
        ? Tourney.sublist
        : [...m.teams[0].players, ...m.teams[1].players];

    let t1MalePlayers = m.teams[0].players.filter(p => p.gender === "male").slice(0, 2);
    let t2MalePlayers = m.teams[1].players.filter(p => p.gender === "male").slice(0, 2);
    let t1FemalePlayers = m.teams[0].players.filter(p => p.gender === "female").slice(0, 2);
    let t2FemalePlayers = m.teams[1].players.filter(p => p.gender === "female").slice(0, 2);

    let onSave = async () => {
        let pmatchStatuses = m.pmatches.map(pm => pm?.status || 'scheduled');

        // Set player_ids for MD (pmatches[0])
        m.pmatches[0].player_ids = [
            t1MalePlayers[0]?.id,
            t1MalePlayers[1]?.id,
            t2MalePlayers[0]?.id,
            t2MalePlayers[1]?.id
        ];

        // Set player_ids for WD (pmatches[1])
        m.pmatches[1].player_ids = [
            t1FemalePlayers[0]?.id,
            t1FemalePlayers[1]?.id,
            t2FemalePlayers[0]?.id,
            t2FemalePlayers[1]?.id
        ];

        await callWithCatch({
            url: "/tmatches/" + m.id,
            method: "put",
            params: {
                scores: m.scores,
                player_ids: m.player_ids,
                pmatch_statuses: pmatchStatuses,
                pmatches: m.pmatches.map(pm => ({
                    id: pm.id,
                    scores: pm.scores,
                    player_ids: (pm.player_ids || []).map(id => id || 0),
                    status: pm.status
                }))
            },
            onSuccess: data => {
                onUpdate(data.reload_info);
                notification.success({ message: `Match Saved!` });

                NiceModal.remove(MatchEditor);
            },
        })
    }

    let max = m.format === "to_15" ? 15 : 11;

    return <NiceModalForm onSave={onSave}
                          title={m.name + " (#" + m.mid + ")"}
                          top
                          width={800}
                          okText="Save"
                          vertical
                          initialValues={{}}>
        <FakeTable small headers={["Game", m.teams[0].name, "Score", "Score", m.teams[1].name]}>
            <tr>
                <td style={{width: "100px"}}>MD</td>
                <td style={{width: "300px"}}>
                    {t1MalePlayers.map(p => (
                        <PlayerWithSub key={p.id} player={p} team={m.teams[0]} allPlayers={allPlayers} subs={m.subs} matchId={m.id} onUpdate={onUpdate} />
                    ))}
                </td>
                <td><SingleGameScoreBtn g={m.pmatches[0].scores[0]} ti={0} max={max}/></td>
                <td><SingleGameScoreBtn g={m.pmatches[0].scores[0]} ti={1} max={max}/></td>
                <td style={{width: "300px"}}>
                    {t2MalePlayers.map(p => (
                        <PlayerWithSub key={p.id} player={p} team={m.teams[1]} allPlayers={allPlayers} subs={m.subs} matchId={m.id} onUpdate={onUpdate} />
                    ))}
                </td>
            </tr>
            <tr>
                <td>WD</td>
                <td>
                    {t1FemalePlayers.map(p => (
                        <PlayerWithSub key={p.id} player={p} team={m.teams[0]} allPlayers={allPlayers} subs={m.subs} matchId={m.id} onUpdate={onUpdate} />
                    ))}
                </td>
                <td><SingleGameScoreBtn g={m.pmatches[1].scores[0]} ti={0} max={max}/></td>
                <td><SingleGameScoreBtn g={m.pmatches[1].scores[0]} ti={1} max={max}/></td>
                <td>
                    {t2FemalePlayers.map(p => (
                        <PlayerWithSub key={p.id} player={p} team={m.teams[1]} allPlayers={allPlayers} subs={m.subs} matchId={m.id} onUpdate={onUpdate} />
                    ))}
                </td>
            </tr>
            {[2, 3, 4, 5].map(gameIndex => (
                <tr key={gameIndex}>
                    <td>MXD #{gameIndex - 1}</td>
                    <td>
                        <div className="space-y-2">
                            <PlayerSelector t={m.teams[0]} value={m.pmatches[gameIndex].player_ids[0]} 
                                          onChange={id => m.pmatches[gameIndex].player_ids[0] = id} 
                                          gender="male" />
                            <PlayerSelector t={m.teams[0]} value={m.pmatches[gameIndex].player_ids[1]} 
                                          onChange={id => m.pmatches[gameIndex].player_ids[1] = id}
                                          gender="female" />
                        </div>
                    </td>
                    <td><SingleGameScoreBtn g={m.pmatches[gameIndex].scores[0]} ti={0} max={max}/></td>
                    <td><SingleGameScoreBtn g={m.pmatches[gameIndex].scores[0]} ti={1} max={max}/></td>
                    <td>
                        <div className="space-y-2">
                            <PlayerSelector t={m.teams[1]} value={m.pmatches[gameIndex].player_ids[2]} 
                                          onChange={id => m.pmatches[gameIndex].player_ids[2] = id}
                                          gender="male" />
                            <PlayerSelector t={m.teams[1]} value={m.pmatches[gameIndex].player_ids[3]} 
                                          onChange={id => m.pmatches[gameIndex].player_ids[3] = id}
                                          gender="female" />
                        </div>
                    </td>
                </tr>
            ))}
        </FakeTable>
    </NiceModalForm>
});

export let TeamName = ({t}) => {
    return <Switch2 v={t.name}>
        <div>{t.name}</div>
        {t.players.map(p => <div key={p.id}>{p.name}</div>)}
    </Switch2>
}

export default MatchEditor;